import React, { useEffect, useState } from 'react'
import Image from 'next/legacy/image'
import styled from 'styled-components'
import TagManager from 'react-gtm-module'
import {
  ContentsBtn,
  ContentsContainer,
  ContentsGuideCard,
  ContentsGuideText,
  ContentsSubDescText,
  ContentsWrapper,
  SemiBold14Text,
} from 'components/resume-analyzer/styles'
import { BoxInput } from 'components/common/input'
import { BtnType } from 'components/common/button'
import { Column } from 'components/common/layout'
import {
  MarketingContentsShare,
  shareTwitter,
} from 'components/resume-analyzer/share'
import { postFormRequest } from 'apis/fetcher'
import { FileInputBox, FileSelectedBox } from 'components/apply/file-input'
import { useRouter } from 'next/router'
import { clearAllFiles, saveFile } from 'infra/indexeddb'
import { FullPageLoader } from 'components/common/loading-spinner'
import {
  HOST_BASE_URL,
  MARKETING_CONTENT_JOB_MATCH_DESC,
  MARKETING_JOB_MATCH_SHARED_KEY,
} from 'infra/constants'
import { BoldText, MediumText } from 'components/common/text'
import { Seo } from 'components/common/seo'
import { Envelope } from 'types/common'
import { getMarketingContentSharedStorage } from 'utils/storages'
import { MarketingImageBanner } from 'components/resume-analyzer/banner'
import { getPositionHtmlFile } from 'utils/formatters'
import { RoutePaths } from 'types/router'
import { getKakaoShareParams, shareKakao } from 'infra/kakao-share'

const ContentsJobMatch: React.FC = () => {
  const router = useRouter()
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [positionLink, setPositionLink] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const kakaoShareParams = getKakaoShareParams({
    title: '내 이력서 서류 합격률 예측하기',
    description: MARKETING_CONTENT_JOB_MATCH_DESC,
    imageUrl: '/c-banner.png',
    link: router.asPath,
    btnTitle: '테스트 하러가기',
  })

  const uploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    file && setResumeFile(file)
  }

  const submit = async () => {
    try {
      if (!resumeFile) throw '파일을 첨부해주세요.'
      const storage = getMarketingContentSharedStorage(
        MARKETING_JOB_MATCH_SHARED_KEY,
      )
      if (storage?.isShared === false)
        throw '친구에게 공유하면 다시 이용할 수 있어요.'
      setIsLoading(true)
      const positionHtmlFile = await getPositionHtmlFile(positionLink)
      const formData = new FormData()
      formData.append('resumeFile', resumeFile)
      formData.append('positionHtmlFile', positionHtmlFile)
      const { data }: Envelope<{ id: number }> = await postFormRequest(
        '/resume-analysis/character-match',
        formData,
      )
      await clearAllFiles()
      await saveFile(resumeFile)
      localStorage.setItem(
        MARKETING_JOB_MATCH_SHARED_KEY,
        JSON.stringify({
          id: data?.id || 0,
          isShared: false,
        }),
      )
      TagManager.dataLayer({
        dataLayer: {
          event: 'button_click_start',
        },
      })
      router.push(`${RoutePaths.RESUME_ANALYZER_JOB_MATCH}/${data?.id}`)
    } catch (e) {
      alert(e)
    } finally {
      setIsLoading(false)
    }
  }

  const setStorageIsShared = () => {
    const storage = getMarketingContentSharedStorage(
      MARKETING_JOB_MATCH_SHARED_KEY,
    )
    localStorage.setItem(
      MARKETING_JOB_MATCH_SHARED_KEY,
      JSON.stringify({ id: storage?.id, isShared: true }),
    )
  }

  useEffect(() => {
    if (router.query.positionLink)
      setPositionLink(router.query.positionLink as string)
  }, [router.query])

  return (
    <>
      <Seo
        title={kakaoShareParams.title}
        description={kakaoShareParams.description}
        image={kakaoShareParams.imageUrl}
        url={kakaoShareParams.link}
      />
      {isLoading && (
        <FullPageLoader>이력서를 분석하고 있어요 . . . </FullPageLoader>
      )}
      <ContentsContainer>
        <ContentsWrapper>
          <Column style={{ gap: 40 }}>
            <div style={{ flex: 'none' }}>
              <Image
                width={150}
                height={20}
                src='/logo2.svg'
                alt='GroupBy Logo'
                style={{ cursor: 'pointer' }}
                onClick={() => router.push(HOST_BASE_URL as string)}
              />
            </div>
            <Column style={{ gap: 4 }}>
              <ContentsSubTitleText>
                내 이력서로 서류합격을 할 수 있을까?!
              </ContentsSubTitleText>
              <ContentsMainTitleText>
                내 이력서 <span>서류 합격률</span> 예측하기
              </ContentsMainTitleText>
            </Column>
            <ContentsSubDescText>
              나의 이력서와 궁금한 채용링크를 넣어보세요.
              <br />
              채용공고를 바탕으로 이력서를 분석해드립니다.
            </ContentsSubDescText>
          </Column>
          <Column style={{ gap: 60 }}>
            <Column style={{ gap: 8 }}>
              <Column style={{ gap: 4 }}>
                <SemiBold14Text>내 이력서를 첨부해주세요</SemiBold14Text>
                <SemiBold14Text>
                  (이력서는 외부에 공개되지 않습니다)
                </SemiBold14Text>
              </Column>
              {resumeFile ? (
                <FileSelectedBox
                  file={resumeFile}
                  onRemove={() => setResumeFile(null)}
                />
              ) : (
                <FileInputBox onChangeEvent={uploadFile} isRequirePDF />
              )}
              <ContentsGuideCard>
                <ContentsGuideText>
                  * 텍스트 인식이 가능한 PDF 파일을 첨부해주세요
                  <br />
                  PDF가 이미지 형식인 경우 분석이 어려울 수 있어요
                </ContentsGuideText>
              </ContentsGuideCard>
            </Column>
            <Column style={{ gap: 8 }}>
              <SemiBold14Text>채용 공고 링크를 첨부해주세요</SemiBold14Text>
              <BoxInput
                placeholder='https://example.com'
                value={positionLink}
                onChange={(e) => setPositionLink(e.target.value)}
              />
            </Column>
          </Column>
          <Column style={{ gap: 80 }}>
            <ContentsBtn
              as='a'
              variant={BtnType.SECONDARY}
              onClick={submit}
              data-gtm-click-text='분석 시작'
            >
              🚀 분석 시작!
            </ContentsBtn>
            <MarketingContentsShare
              onClickKakao={() => {
                shareKakao(kakaoShareParams)
                setStorageIsShared()
              }}
              onClickTwitter={() => {
                shareTwitter(kakaoShareParams.title, kakaoShareParams.link)
                setStorageIsShared()
              }}
            />
          </Column>
        </ContentsWrapper>
        <MarketingImageBanner url='https://groupby.kr/greeting?utm_medium=organic&utm_source=event_banner&utm_campaign=event_c&utm_content=%EC%84%9C%EB%A5%98%ED%95%A9%EA%B2%A9%EB%A5%A0' />
      </ContentsContainer>
    </>
  )
}

const ContentsMainTitleText = styled(BoldText)`
  font-size: ${(p) => p.theme.fontSize.h3};
  span {
    color: ${(p) => p.theme.color.secondary};
  }
`

const ContentsSubTitleText = styled(MediumText)`
  line-height: 1.6;
  font-size: ${(p) => p.theme.fontSize.desc};
`

export default ContentsJobMatch
